import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { downloadBlobFromUrl } from '../../utils/downloadUtils';
import { fetchConsentHistory, fetchConsentAuditTrailFile } from './consentAuditApiHelper';
import { CONSENT_AUDIT_ERRORS } from './constants';
import { ConsentHistoryEntry, ConsentQueryParams } from './types';

/**
 * Open/close, fetch and download state for the Consent Audit drawer. `orgName` arrives WITH
 * the history response (never passed to `openDrawer`) since it's resolved server-side; the
 * download filename likewise comes from the `/consent/auditTrail` response's
 * `Content-Disposition` header rather than being rebuilt client-side.
 */
export default function useConsentAuditDrawer() {
    const [open, setOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadFailed, setLoadFailed] = useState<boolean>(false);
    const [downloading, setDownloading] = useState<boolean>(false);
    const [entries, setEntries] = useState<ConsentHistoryEntry[]>([]);
    const [orgName, setOrgName] = useState<string>('');
    const [context, setContext] = useState<ConsentQueryParams | null>(null);

    const requestGeneration = useRef(0);

    const openDrawer = useCallback(async (params: ConsentQueryParams) => {
        const generation = ++requestGeneration.current;
        setContext(params);
        setOpen(true);
        setLoading(true);
        setLoadFailed(false);
        setEntries([]);
        setOrgName('');

        const result = await fetchConsentHistory(params);
        if (generation !== requestGeneration.current) return;

        if (result.success) {
            setEntries(result.entries);
            setOrgName(result.orgName);
        } else {
            setLoadFailed(true);
            toast.error(result.message || CONSENT_AUDIT_ERRORS.LOAD_FAILED);
        }
        setLoading(false);
    }, []);

    const closeDrawer = useCallback(() => {
        // Invalidate any in-flight request from this drawer session so its response can never
        // land after close/reopen and overwrite the next session's state.
        requestGeneration.current += 1;
        setOpen(false);
        setContext(null);
        setEntries([]);
        setOrgName('');
        setLoadFailed(false);
    }, []);

    const downloadAuditTrail = useCallback(async () => {
        if (!context) return;
        setDownloading(true);
        const file = await fetchConsentAuditTrailFile(context);
        if (file) {
            downloadBlobFromUrl(file.blob, file.filename);
        } else {
            toast.error(CONSENT_AUDIT_ERRORS.DOWNLOAD_FAILED);
        }
        setDownloading(false);
    }, [context]);

    return { open, loading, loadFailed, downloading, entries, orgName, context, openDrawer, closeDrawer, downloadAuditTrail };
}
